import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import "./ButtonList.css";
import ButtonCard from "../ButtonCard/ButtonCard.jsx";
import EaseKey from "../EaseKey/EaseKey.jsx";
import ParagraphButtons from "../ParagraphButtons/ParagraphButtons.jsx";
import EaseCopyPasteButtons from "../EaseCopyPasteButtons/EaseCopyPasteButtons.jsx";
import AnchorAlignNull from "../AnchorAlignNull/AnchorAlignNull.jsx";
import ChangeCompositionSettings from "../ChangeCompositionSettings/ChangeCompositionSettings.jsx";
import COMPONENTS from "../../utils/components.js";
import { usePersistentState } from "../../hooks/usePersistentState.jsx";
import { hasCategory } from "../../utils/subscription.js";

const useDynamicPositioning = (targetSelector, navSelector, refreshKey = "") => {
  useLayoutEffect(() => {
    let frameId = null;
    const targetElement = document.querySelector(targetSelector);
    const navElement = document.querySelector(navSelector);

    if (!targetElement || !navElement) {
      return undefined;
    }

    const setStyleValue = (element, property, value) => {
      if (element.style[property] !== value) {
        element.style[property] = value;
      }
    };

    const updatePosition = () => {
      const targetRect = targetElement.getBoundingClientRect();
      setStyleValue(navElement, "top", `${targetRect.bottom}px`);
      if (navSelector === ".buttons-card") {
        setStyleValue(
          navElement,
          "paddingBottom",
          `${targetRect.bottom + window.scrollY}px`,
        );
      }
    };

    const scheduleUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(updatePosition);
    };

    updatePosition();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;
    const mutationObserver = new MutationObserver(scheduleUpdate);

    resizeObserver?.observe(targetElement);
    mutationObserver.observe(targetElement, {
      attributes: true,
      attributeFilter: ["style", "class"],
      subtree: true,
    });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [targetSelector, navSelector, refreshKey]);
};

const normalizeButtonName = (name = "") =>
  name.toLowerCase().replace(/[^a-zA-Z0-9]+/g, "");

const GRID_POPUP_MARGIN = 8;
const GRID_POPUP_MAX_WIDTH = 760;
const GRID_POPUP_MIN_HEIGHT = 140;
const GRID_POPUP_MAX_HEIGHT = 820;
const GRID_ICON_ONLY_MAX_WIDTH = 360;

const getGridPopupElement = (group) =>
  group.querySelector(
    [
      ".render-group:not(.render-group--hidden):not(.render-group--modal-host)",
      ".resolution-tools:not(.hidden)",
      ".colors-schemes",
    ].join(","),
  );

const positionGridPopup = (toggle) => {
  if (!(toggle instanceof HTMLElement)) {
    return;
  }

  const group = toggle.closest(".button-group");
  if (!(group instanceof HTMLElement)) {
    return;
  }

  const rect = toggle.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const popupWidth = Math.max(
    0,
    Math.min(GRID_POPUP_MAX_WIDTH, viewportWidth - GRID_POPUP_MARGIN * 2),
  );
  const left = Math.min(
    Math.max(
      rect.left + rect.width / 2,
      GRID_POPUP_MARGIN + popupWidth / 2,
    ),
    viewportWidth - GRID_POPUP_MARGIN - popupWidth / 2,
  );
  const spaceBelow = viewportHeight - rect.bottom - GRID_POPUP_MARGIN;
  const spaceAbove = rect.top - GRID_POPUP_MARGIN;
  const popupElement = getGridPopupElement(group);
  const desiredHeight = Math.min(
    GRID_POPUP_MAX_HEIGHT,
    popupElement?.scrollHeight || GRID_POPUP_MIN_HEIGHT,
    viewportHeight - GRID_POPUP_MARGIN * 2,
  );
  const openAbove =
    spaceBelow < desiredHeight && spaceAbove > spaceBelow;
  const availableHeight = Math.max(0, openAbove ? spaceAbove - 8 : spaceBelow - 8);
  const popupMaxHeight = Math.min(
    GRID_POPUP_MAX_HEIGHT,
    Math.max(80, availableHeight),
    viewportHeight - GRID_POPUP_MARGIN * 2,
  );

  group.style.setProperty("--grid-popup-left", `${left}px`);
  group.style.setProperty(
    "--grid-popup-top",
    `${openAbove ? rect.top - 8 : rect.bottom + 8}px`,
  );
  group.style.setProperty("--grid-popup-width", `${popupWidth}px`);
  group.style.setProperty("--grid-popup-max-height", `${popupMaxHeight}px`);
  group.style.setProperty(
    "--grid-popup-offset-y",
    openAbove ? "-100%" : "0",
  );
};

const ButtonList = ({
  currentUser,
  useVisible,
  saveCepStorage,
  getCepStorage,
  useCepStorage,
  buttons,
  search,
  selectedCategory,
  selectedTag,
  tags,
  buttonViewMode = "list",
}) => {
  const [activeSlide, setActiveSlide] = useVisible("activeSlide");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const scrollContainerRef = useRef(null);
  const normalizedButtonViewMode = buttonViewMode === "grid" ? "grid" : "list";
  const showGridButtonTooltip =
    normalizedButtonViewMode === "grid" &&
    windowWidth <= GRID_ICON_ONLY_MAX_WIDTH;
  const [scrollPositions, setScrollPositions] = usePersistentState(
    "buttonsScrollPositions",
    {},
    {
      serializer: JSON.stringify,
      validator: (value) =>
        value && typeof value === "object" && !Array.isArray(value)
          ? value
          : {},
    },
  );
  const scrollPositionsRef = useRef(scrollPositions);
  const layoutRefreshKey = [
    normalizedButtonViewMode,
    selectedCategory,
    selectedTag || "",
    buttons.length,
    tags.length,
  ].join(":");

  useDynamicPositioning(
    ".tags-container__tags",
    ".additional__navigation",
    layoutRefreshKey,
  );
  useDynamicPositioning(
    ".additional__navigation",
    ".sliderContainer",
    layoutRefreshKey,
  );
  useDynamicPositioning(
    ".sliderContainer",
    ".paragraphs-cards",
    layoutRefreshKey,
  );
  useDynamicPositioning(
    ".paragraphs-cards",
    ".ease-copy-paste-buttons",
    layoutRefreshKey,
  );
  useDynamicPositioning(
    ".ease-copy-paste-buttons",
    ".buttons-card",
    layoutRefreshKey,
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    scrollPositionsRef.current = scrollPositions;
  }, [scrollPositions]);

  useEffect(() => {
    if (normalizedButtonViewMode !== "grid") {
      return undefined;
    }

    const gridList = scrollContainerRef.current?.querySelector(
      ".buttons-card__list--grid",
    );
    const syncGridBackdrop = () => {
      const hasActiveGridPopup = Boolean(
        document.querySelector(
          ".buttons-card__list--grid .resolution-toggle.active",
        ),
      );
      gridList?.classList.toggle(
        "buttons-card__list--grid-has-popup",
        hasActiveGridPopup,
      );
    };

    const updateActiveGridPopup = () => {
      const activeToggle = document.querySelector(
        ".buttons-card__list--grid .resolution-toggle.active",
      );
      positionGridPopup(activeToggle);
      syncGridBackdrop();
    };

    const closeOpenGridPopups = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const clickedToggle = target.closest(
        ".buttons-card__list--grid .resolution-toggle",
      );
      positionGridPopup(clickedToggle);

      const activePopup = target.closest(
        [
          ".buttons-card__list--grid > .button-group > .render-group:not(.render-group--hidden):not(.render-group--modal-host)",
          ".buttons-card__list--grid > .button-group > .resolution-tools:not(.hidden)",
          ".buttons-card__list--grid > .button-group > .colors-schemes",
          ".modal-overlay",
          ".effects-popup-overlay",
        ].join(","),
      );

      if (activePopup) {
        syncGridBackdrop();
        return;
      }

      const activeToggles = Array.from(
        document.querySelectorAll(
          ".buttons-card__list--grid .resolution-toggle.active",
        ),
      );
      const isSwitchingPopup =
        clickedToggle &&
        activeToggles.some((toggle) => toggle !== clickedToggle);
      const clickedGridList = clickedToggle?.closest(".buttons-card__list--grid");

      if (isSwitchingPopup && clickedGridList instanceof HTMLElement) {
        clickedGridList.classList.add("buttons-card__list--grid-popup-switching");
        window.setTimeout(() => {
          clickedGridList.classList.remove(
            "buttons-card__list--grid-popup-switching",
          );
        }, 120);
      }

      activeToggles.forEach((toggle) => {
        if (toggle === clickedToggle) {
          return;
        }

        if (toggle instanceof HTMLElement) {
          toggle.click();
        }
      });

      syncGridBackdrop();
    };

    const gridMutationObserver = new MutationObserver(updateActiveGridPopup);
    if (gridList) {
      gridMutationObserver.observe(gridList, {
        attributes: true,
        attributeFilter: ["class"],
        subtree: true,
      });
      syncGridBackdrop();
    }

    document.addEventListener("mousedown", closeOpenGridPopups, true);
    window.addEventListener("resize", updateActiveGridPopup);
    const container = scrollContainerRef.current;
    container?.addEventListener("scroll", updateActiveGridPopup, {
      passive: true,
    });

    return () => {
      document.removeEventListener("mousedown", closeOpenGridPopups, true);
      window.removeEventListener("resize", updateActiveGridPopup);
      container?.removeEventListener("scroll", updateActiveGridPopup);
      gridMutationObserver.disconnect();
      gridList?.classList.remove(
        "buttons-card__list--grid-has-popup",
        "buttons-card__list--grid-popup-switching",
      );
    };
  }, [normalizedButtonViewMode, layoutRefreshKey]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      const nextPosition = container.scrollTop;
      setScrollPositions((prev) => {
        const currentValue = prev[selectedCategory];
        if (currentValue === nextPosition) {
          return prev;
        }
        return { ...prev, [selectedCategory]: nextPosition };
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedCategory, setScrollPositions]);

  const componentMap = useMemo(() => {
    const map = {};
    Object.keys(COMPONENTS).forEach((key) => {
      map[key.toLowerCase()] = COMPONENTS[key];
    });
    return map;
  }, []);

  const filteredButtons = useMemo(() => {
    const normalizedSearch = (search || "").trim().toLowerCase();

    return buttons.filter((button) => {
      const hasAccess =
        hasCategory(currentUser.subscription, button.category) ||
        currentUser.role === "admin" ||
        button.access === "free";

      if (normalizedSearch) {
        const isGroupButton =
          componentMap[normalizeButtonName(button.name)] !== undefined;

        if (isGroupButton) {
          return hasAccess;
        }

        return button.name.toLowerCase().includes(normalizedSearch) && hasAccess;
      }

      return button.category === selectedCategory && hasAccess;
    });
  }, [
    buttons,
    search,
    selectedCategory,
    currentUser.subscription,
    currentUser.role,
    componentMap,
  ]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const savedScroll = scrollPositionsRef.current[selectedCategory] || 0;
    const restoreScroll = () => {
      const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      const nextScroll = Math.min(savedScroll, maxScroll);
      if (container.scrollTop !== nextScroll) {
        container.scrollTop = nextScroll;
      }
    };

    restoreScroll();
    const frameId = requestAnimationFrame(restoreScroll);

    return () => cancelAnimationFrame(frameId);
  }, [
    selectedCategory,
    filteredButtons.length,
    windowWidth,
    normalizedButtonViewMode,
  ]);

  const toggleNavigation = (indx) =>
    setActiveSlide(activeSlide === indx ? false : indx);

  const additionalNavigation = [
    {
      component: <EaseKey />,
      icon: <i className="fa-regular fa-rhombus"></i>,
      helpTip: "Keyframe Easing",
    },
    {
      component: <AnchorAlignNull script={"$.ae.moveAnchor"} />,
      icon: <i className="fa-solid fa-anchor"></i>,
      helpTip: "Anchor Point",
    },
    {
      component: <AnchorAlignNull script={"alignTo"} />,
      icon: (
        <i className="fa-sharp fa-solid fa-objects-align-center-vertical"></i>
      ),
      helpTip: "Align Layers",
    },
    {
      component: <AnchorAlignNull script={"newNull"} />,
      icon: <i className="fa-sharp fa-solid fa-layer-plus"></i>,
      helpTip: "Add Null",
    },
    {
      component: <ChangeCompositionSettings />,
      icon: <i className="fa-regular fa-gear"></i>,
      helpTip: "Comp Setting",
    },
  ];

  return (
    <>
      <div className="additional__navigation">
        {additionalNavigation.map((obj, indx) => (
          <div
            className={`additional__navigation_item ${
              activeSlide === indx ? "additional__navigation_item--active" : ""
            }`}
            onClick={() => toggleNavigation(indx)}
            title={obj.helpTip}
            key={indx}
          >
            {obj.icon}
          </div>
        ))}
      </div>
      <div className="sliderContainer">
        {additionalNavigation[activeSlide] && (
          <>{additionalNavigation[activeSlide].component}</>
        )}
      </div>
      <ParagraphButtons />
      <EaseCopyPasteButtons currentUser={currentUser} />
      <section
        className={`buttons-card buttons-card--${normalizedButtonViewMode}`}
        ref={scrollContainerRef}
      >
        {filteredButtons.length ? (
          <ul
            className={`buttons-card__list buttons-card__list--${normalizedButtonViewMode}`}
          >
            {filteredButtons.map((button) => {
              const buttonName = normalizeButtonName(button.name);
              const ComponentToRender = componentMap[buttonName];
              const isAdmin = currentUser.role === "admin";
              const isFree = button.access === "free";
              const isUserOrTest =
                currentUser.role === "user" || currentUser.role === "test";
              const isNotAdminButton = button.access !== "admin";
              const hasSubscription =
                isFree || hasCategory(currentUser.subscription, selectedCategory);
              const hasAccess =
                isAdmin ||
                (hasSubscription && isUserOrTest && isNotAdminButton);
              return hasAccess ? (
                <ButtonCard
                  currentUser={currentUser}
                  saveCepStorage={saveCepStorage}
                  getCepStorage={getCepStorage}
                  useCepStorage={useCepStorage}
                  useVisible={useVisible}
                  key={button._id}
                  button={button}
                  selectedTag={selectedTag}
                  tags={tags}
                  search={search}
                  ComponentToRender={ComponentToRender}
                  buttonViewMode={normalizedButtonViewMode}
                  showGridButtonTooltip={showGridButtonTooltip}
                />
              ) : null;
            })}
          </ul>
        ) : null}
      </section>
    </>
  );
};

export default ButtonList;
